import { useEffect, useState } from "react";
import {
  cacheAll, cacheSize, clearCache, commentaryPaths, crowdPaths, musicPaths,
  cacheClubData, type Progress,
} from "../media.js";
import { getPack } from "../voice.js";
import { Card, Button } from "../ui.js";
import { IconCheck, IconOffline } from "../Icons.js";

/**
 * Download everything for a ground with no signal.
 *
 * The app already works offline for scoring — deliveries queue and sync later.
 * What it could not do is *sound* right offline, because the commentary and the
 * music were fetched on demand. This pulls the lot down on request: a few
 * hundred clips, the crowd, and the club's own uploads.
 *
 * It is a button rather than automatic, because it is tens of megabytes and the
 * person holding the phone may be on a metered connection at the time.
 */
export function OfflineAudio({ clubId }: { clubId: string }) {
  const [have, setHave] = useState<{ files: number; bytes: number } | null>(null);
  const [busy, setBusy] = useState<Progress | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const refresh = () => { void cacheSize().then(setHave); };
  useEffect(refresh, []);

  const download = async () => {
    setDone(null);
    // Data first: it is small, and an empty match list at a ground is worse
    // than missing commentary.
    setBusy({ done: 0, total: 1, failed: 0 });
    const dataFiles = await cacheClubData(clubId);
    const paths = [
      ...crowdPaths(),
      ...(await commentaryPaths(getPack("en"))),
      ...(await commentaryPaths(getPack("hi"))),
      ...(await musicPaths(clubId)),
    ];
    setBusy({ done: 0, total: paths.length, failed: 0 });
    const r = await cacheAll(paths, setBusy);
    setBusy(null);
    refresh();
    setDone(r.failed
      ? `${r.done} of ${r.total} clips saved, plus ${dataFiles} screens — ${r.failed} failed.`
      : `${r.done} clips and ${dataFiles} screens are on this device.`);
  };

  const mb = have ? (have.bytes / 1048576).toFixed(1) : "0";

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-faint"><IconOffline size={20} /></span>
          <div className="min-w-0 flex-1">
            <b className="block text-body font-semibold">Play without signal</b>
            <p className="mt-1 text-caption leading-relaxed text-faint">
              Scoring already works offline — deliveries queue and send themselves when you are
              back. This saves everything else: the commentary, the crowd, your uploaded music,
              and the club's teams, players and matches, so nothing is missing at a ground with
              no bars.
            </p>
          </div>
        </div>

        {have && have.files > 0 && (
          <div className="mt-3 flex items-center gap-2 font-mono text-meta text-crease">
            <IconCheck size={13} /> {have.files} files · {mb} MB on this device
          </div>
        )}

        {busy && (
          <div className="mt-3">
            <div className="h-[3px] overflow-hidden rounded bg-line">
              <div className="h-full bg-amber transition-all"
                   style={{ width: `${Math.round((busy.done / Math.max(1, busy.total)) * 100)}%` }} />
            </div>
            <p className="mt-1.5 font-mono text-meta text-faint">
              {busy.done} of {busy.total}
              {busy.failed > 0 && <span className="text-leather"> · {busy.failed} failed</span>}
            </p>
          </div>
        )}

        {done && <p className="mt-3 text-caption text-crease">{done}</p>}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button full disabled={!!busy} onClick={download}>
            {busy ? "Saving…" : "Save for offline"}
          </Button>
          <Button full kind="ghost" disabled={!!busy || !have?.files}
            onClick={async () => { await clearCache(); refresh(); setDone(null); }}>
            Clear
          </Button>
        </div>
      </div>
    </Card>
  );
}
